import api from "../../services/baseapi";

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ScorecardStatus = "draft" | "published" | "outdated";
export type ScoringMode = "weighted_average" | "equal_weight";
export type TrafficLight = "green" | "yellow" | "red" | null;
export type UpdateMode = "live" | "snapshot" | "manual";
export type ValueSource = "manual" | "formula" | "api";
export type ValueScale =
  | "percentage"
  | "score_0_10"
  | "score_1_5"
  | "count"
  | "custom";
export type BenchmarkSource = "manual" | "library";
export type ApprovalStatus = "pending" | "accepted" | "rejected";

// ── Dynamic range (score bands) ───────────────────────────────────────────────

export interface DynamicRangeBand {
  from: number;
  to: number;
  label: string;
  color: string;
  description?: string;
}

// ── Live diff ─────────────────────────────────────────────────────────────────

export interface LiveDiff {
  has_unpublished_changes: boolean;
  changed_indicator_count: number;
  published_overall_score: number | null;
  live_overall_score: number | null;
  overall_delta: number | null;
  domains: Array<{
    id: string;
    name: string;
    published_score: number | null;
    live_score: number | null;
    delta: number | null;
    changed: boolean;
  }>;
  changed_indicators: Array<{
    id: string;
    name: string;
    domain_name: string;
    published_value: number | null;
    live_value: number | null;
    delta: number | null;
  }>;
}

// ── Core types ────────────────────────────────────────────────────────────────

export interface ApiScorecard {
  id: string;
  project_id: string;
  title: string;
  scoring_mode: ScoringMode;
  status: ScorecardStatus;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  overall_benchmark_upper: number | null;
  overall_benchmark_lower: number | null;
  published_at: string | null;
  published_by: number | null;
  summary: {
    green: number;
    yellow: number;
    red: number;
    total: number;
  };
  live_diff: LiveDiff | null;
}

export interface ApiDomain {
  id: string;
  scorecard_id: string;
  name: string;
  weight: number;
  domain_score: number | null;
  traffic_light: TrafficLight;
  color: string;
  sort_order: number;
  description?: string | null;
  benchmark_upper?: number | null;
  benchmark_lower?: number | null;
}

export interface ApiIndicator {
  id: string;
  domain_id: string;
  name: string;
  value_source: ValueSource;
  linked_formula_id: string | null;
  current_value: number | null;
  normalised_score: number | null;
  value_scale: ValueScale;
  scale_min?: number | null;
  scale_max?: number | null;
  weight: number;
  target_value: number | null;
  benchmark_upper: number | null;
  benchmark_lower: number | null;
  benchmark_source: BenchmarkSource;
  linked_benchmark_id: string | null;
  external_benchmark_label: string | null;
  external_benchmark_url: string | null;
  dynamic_range: DynamicRangeBand[] | null;
  current_band: string | null;
  traffic_light: TrafficLight;
  update_mode: UpdateMode;
  notes?: string | null;
  approval_status: ApprovalStatus;
  approval_note: string | null;
  approved_by: number | null;
  approved_at: string | null;
  last_updated_at: string | null;
  sort_order: number;
}

export interface ApiDomainWithIndicators extends ApiDomain {
  indicators: ApiIndicator[];
}

export interface ApiScorecardFull extends ApiScorecard {
  domains: ApiDomainWithIndicators[];
}

// ── Publish ───────────────────────────────────────────────────────────────────

export interface PublishResponse {
  scorecard_id: string;
  status: string;
  version_number: number;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  published_at: string;
  published_by: number;
  pinned: boolean | null;
}

// ── History ───────────────────────────────────────────────────────────────────

export interface HistorySnapshot {
  id: string;
  version_number: number;
  version_label: string;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  overall_delta: number | null;
  published_at: string;
  published_by: { id: number; name: string };
  domains: Array<{
    name: string;
    domain_score: number | null;
    traffic_light: TrafficLight;
    color: string;
    delta: number | null;
  }>;
}

export interface HistoryTrendPoint {
  version_number: number;
  version_label: string;
  overall_score: number | null;
  published_at: string;
  domains: Array<{ name: string; domain_score: number | null; color: string }>;
}

export interface HistoryListResponse {
  current_version: number;
  snapshots: HistorySnapshot[];
  trend: HistoryTrendPoint[];
}

export interface HistorySnapshotFull {
  id: string;
  version_number: number;
  version_label: string;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  published_at: string;
  published_by: { id: number; name: string };
  snapshot_data: ApiDomainWithIndicators[];
}

export interface CompareResponse {
  v1: { id: string; version_number: number; overall_score: number | null };
  v2: { id: string; version_number: number; overall_score: number | null };
  overall_delta: number | null;
  domains: Array<{
    name: string;
    score_v1: number | null;
    score_v2: number | null;
    delta: number | null;
    traffic_light_v1: TrafficLight;
    traffic_light_v2: TrafficLight;
    indicators: Array<{
      name: string;
      value_v1: number | null;
      value_v2: number | null;
      delta: number | null;
      traffic_light_v1: TrafficLight;
      traffic_light_v2: TrafficLight;
    }>;
  }>;
}

// ── Templates ─────────────────────────────────────────────────────────────────

export interface ApiTemplate {
  id: string;
  name: string;
  best_for: string;
  domain_count: number;
}

export interface ApiTemplateDetail extends ApiTemplate {
  template_data: {
    domains: Array<{
      name: string;
      weight: number;
      color: string;
      indicators: Array<{ name: string; weight?: number }>;
    }>;
  };
}

// ── Benchmark library ─────────────────────────────────────────────────────────

export interface ApiBenchmarkLibraryEntry {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  value_scale?: ValueScale | null;
  benchmark_lower?: number | null;
  benchmark_upper?: number | null;
  target_value?: number | null;
  source_label?: string | null;
  source_url?: string | null;
  is_active: boolean;
}

// ── Linkable formulas ─────────────────────────────────────────────────────────

export interface LinkableFormula {
  id: string;
  name: string;
  status: string;
  is_named_result: boolean;
  last_result: number | null;
  updated_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// ── Scorecard ─────────────────────────────────────────────────────────────────

export const getScorecard = async (
  projectId: string,
): Promise<ApiScorecardFull | null> => {
  try {
    const { data } = await api.get(`/bsc/projects/${projectId}/scorecard`);
    return data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

export const createScorecard = async (
  projectId: string,
  payload: {
    title: string;
    scoring_mode?: ScoringMode;
    template_id?: string;
    overall_benchmark_upper?: number;
    overall_benchmark_lower?: number;
  },
): Promise<ApiScorecardFull> => {
  const { data } = await api.post(
    `/bsc/projects/${projectId}/scorecard`,
    payload,
  );
  return data;
};

export const updateScorecard = async (
  projectId: string,
  payload: {
    title?: string;
    scoring_mode?: ScoringMode;
    overall_benchmark_upper?: number | null;
    overall_benchmark_lower?: number | null;
  },
): Promise<ApiScorecard> => {
  const { data } = await api.patch(
    `/bsc/projects/${projectId}/scorecard`,
    payload,
  );
  return data;
};

export const publishScorecard = async (
  projectId: string,
  payload: { note?: string; pin?: boolean },
): Promise<PublishResponse> => {
  const { data } = await api.post(
    `/bsc/projects/${projectId}/scorecard/publish`,
    payload,
  );
  return data;
};

export const exportToDashboard = async (
  projectId: string,
  pin = true,
): Promise<{
  scorecard_id: string;
  dashboard_card_id: string;
  pinned: boolean;
}> => {
  const { data } = await api.post(
    `/bsc/projects/${projectId}/scorecard/export-to-dashboard`,
    { pin },
  );
  return data;
};

// ── Domains ───────────────────────────────────────────────────────────────────

export const createDomain = async (
  scorecardId: string,
  payload: {
    name: string;
    weight?: number;
    color?: string;
    description?: string;
    benchmark_upper?: number;
    benchmark_lower?: number;
    sort_order?: number;
  },
): Promise<ApiDomain> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/domains`,
    payload,
  );
  return data;
};

export const updateDomain = async (
  scorecardId: string,
  domainId: string,
  payload: {
    name?: string;
    weight?: number;
    color?: string;
    description?: string;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    sort_order?: number;
  },
): Promise<ApiDomain> => {
  const { data } = await api.patch(
    `/bsc/scorecards/${scorecardId}/domains/${domainId}`,
    payload,
  );
  return data;
};

export const deleteDomain = async (
  scorecardId: string,
  domainId: string,
  options?: { reassign_to?: string; force?: boolean },
): Promise<void> => {
  await api.delete(`/bsc/scorecards/${scorecardId}/domains/${domainId}`, {
    params: options,
  });
};

// ── Indicators ────────────────────────────────────────────────────────────────

export const createIndicator = async (
  domainId: string,
  payload: {
    name: string;
    value_source?: ValueSource;
    linked_formula_id?: string | null;
    current_value?: number | null;
    value_scale?: ValueScale;
    scale_min?: number;
    scale_max?: number;
    weight?: number;
    target_value?: number | null;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    dynamic_range?: DynamicRangeBand[] | null;
    update_mode?: UpdateMode;
    notes?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.post(
    `/bsc/domains/${domainId}/indicators`,
    payload,
  );
  return data;
};

export const updateIndicator = async (
  domainId: string,
  indicatorId: string,
  payload: {
    name?: string;
    value_source?: ValueSource;
    linked_formula_id?: string | null;
    current_value?: number | null;
    value_scale?: ValueScale;
    scale_min?: number | null;
    scale_max?: number | null;
    weight?: number;
    target_value?: number | null;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    dynamic_range?: DynamicRangeBand[] | null;
    update_mode?: UpdateMode;
    notes?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/domains/${domainId}/indicators/${indicatorId}`,
    payload,
  );
  return data;
};

export const deleteIndicator = async (
  domainId: string,
  indicatorId: string,
): Promise<void> => {
  await api.delete(`/bsc/domains/${domainId}/indicators/${indicatorId}`);
};

export const setIndicatorApproval = async (
  domainId: string,
  indicatorId: string,
  payload: { approval_status: ApprovalStatus; approval_note?: string },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/domains/${domainId}/indicators/${indicatorId}/approval`,
    payload,
  );
  return data;
};

export const updateIndicatorBenchmarks = async (
  indicatorId: string,
  payload: {
    benchmark_lower?: number | null;
    benchmark_upper?: number | null;
    target_value?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    external_benchmark_label?: string | null;
    external_benchmark_url?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/indicators/${indicatorId}/benchmarks`,
    payload,
  );
  return data;
};

// ── History ───────────────────────────────────────────────────────────────────

export const getScorecardHistory = async (
  scorecardId: string,
): Promise<HistoryListResponse> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/history`);
  return data;
};

export const getHistorySnapshot = async (
  scorecardId: string,
  versionId: string,
): Promise<HistorySnapshotFull> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/history/${versionId}`,
  );
  return data;
};

export const compareSnapshots = async (
  scorecardId: string,
  v1: string,
  v2: string,
): Promise<CompareResponse> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/history/compare`,
    { params: { v1, v2 } },
  );
  return data;
};

// ── Templates ─────────────────────────────────────────────────────────────────

export const getTemplates = async (): Promise<ApiTemplate[]> => {
  const { data } = await api.get(`/bsc/bsc/templates`);
  return Array.isArray(data?.templates) ? data.templates : [];
};

export const getTemplateDetail = async (
  templateId: string,
): Promise<ApiTemplateDetail> => {
  const { data } = await api.get(`/bsc/bsc/templates/${templateId}`);
  return data;
};

// ── Benchmark library ─────────────────────────────────────────────────────────

export const getBenchmarkLibrary = async (
  projectId: string,
  params?: { search?: string },
): Promise<ApiBenchmarkLibraryEntry[]> => {
  const { data } = await api.get(`/library/projects/${projectId}/references`, {
    params: { category: "benchmark_value", type: "user_created", ...params },
  });
  const items: any[] = Array.isArray(data) ? data : (data.items ?? []);
  return items.map((r) => ({
    id: r.reference_id ?? r.id,
    name: r.title,
    description: r.description ?? null,
    category: r.category ?? null,
    value_scale: null,
    benchmark_lower: null,
    benchmark_upper: null,
    target_value: r.value?.value ?? null,
    source_label: r.citation ?? null,
    source_url: r.value?.source_url ?? null,
    is_active: true,
  }));
};

// ── Linkable formulas ─────────────────────────────────────────────────────────

export const getLinkableFormulas = async (
  projectId: string,
  isNamed = true,
): Promise<LinkableFormula[]> => {
  const { data } = await api.get(
    `/bsc/projects/${projectId}/scorecard/linkable-formulas`,
    { params: { is_named: isNamed } },
  );
  return Array.isArray(data?.formulas) ? data.formulas : [];
};
